import React, { useEffect, useRef, useState } from 'react';
import { AlertCircle, CheckCircle2, Landmark, Link2, Plus, Trash2, UploadCloud } from 'lucide-react';
import { useToast } from '../../../../../hooks/useToast';
import {
  BankAccountRecord,
  ReconcilableMovement,
  DuplicateBankMovementError,
  importBankMovement,
  listBankAccounts,
  listReconcilableMovements,
  reconcileBankMovement
} from '../../../../../services/BankAccountsService';
import { ChartAccount, listChartOfAccounts } from '../../../../../services/ChartOfAccountsService';
import {
  BillingClient,
  InvoiceSummary,
  OPEN_INVOICE_STATUSES,
  collectInvoice,
  listBillingClients,
  listInvoices
} from '../../../../../services/InvoicesService';
import {
  BankJournalEntry,
  JournalLine,
  listBankJournalEntries,
  postJournalEntry
} from '../../../../../services/JournalEntriesService';
import { formatEuro } from '../masters/financeMasters';
import { FormSkeleton, SearchSelect, formatDate, today } from './FormParts';
import '../../shared-modules.css';
import './FinanceForms.css';

const round2 = (n: number) => Math.round(n * 100) / 100;
const sameAmount = (a: number, b: number) => Math.abs(a - b) < 0.005;
const daysBetween = (a: string, b: string) => Math.abs((new Date(a).getTime() - new Date(b).getTime()) / 86400000);
const entryLabel = (id: string) => `AS-2026-${id.padStart(3, '0')}`;

// Cuaderno 43: registros de 80 posiciones

interface C43Movement {
  date: string;
  concept: string;
  amount: number;
}

interface C43Statement {
  fileName: string;
  from: string;
  to: string;
  opening: number;
  closing: number;
  movements: C43Movement[];
}

const c43Date = (yymmdd: string) => `20${yymmdd.slice(0, 2)}-${yymmdd.slice(2, 4)}-${yymmdd.slice(4, 6)}`;
const c43Amount = (digits: string) => (parseInt(digits, 10) || 0) / 100;

// 11 cabecera, 22 movimiento, 23 concepto, 33 saldo final. Clave 1 = cargo, 2 = abono
const parseC43 = (text: string, fileName: string): C43Statement => {
  const statement: C43Statement = { fileName, from: '', to: '', opening: 0, closing: 0, movements: [] };
  let hasConcept = false;
  text.split(/\r?\n/).forEach(line => {
    const code = line.slice(0, 2);
    if (code === '11') {
      statement.from = c43Date(line.slice(20, 26));
      statement.to = c43Date(line.slice(26, 32));
      statement.opening = (line[32] === '1' ? -1 : 1) * c43Amount(line.slice(33, 47));
    } else if (code === '22') {
      statement.movements.push({
        date: c43Date(line.slice(10, 16)),
        concept: `${line.slice(52, 64)} ${line.slice(64, 80)}`.trim(),
        amount: (line[27] === '1' ? -1 : 1) * c43Amount(line.slice(28, 42))
      });
      hasConcept = false;
    } else if (code === '23' && statement.movements.length > 0) {
      const last = statement.movements[statement.movements.length - 1];
      const extra = `${line.slice(4, 42)} ${line.slice(42, 80)}`.replace(/\s+/g, ' ').trim();
      last.concept = hasConcept ? `${last.concept} ${extra}` : extra || last.concept;
      hasConcept = true;
    } else if (code === '33') {
      statement.closing = (line[58] === '1' ? -1 : 1) * c43Amount(line.slice(59, 73));
    }
  });
  if (!statement.from && statement.movements.length === 0) {
    throw new Error('El fichero no tiene formato Cuaderno 43.');
  }
  return statement;
};

// Los movimientos del C43 llevan la fecha de carga en la descripción
const traceOf = (description: string) => {
  const match = description.match(/^\[C43 (\d{4}-\d{2}-\d{2})\] (.*)$/);
  return match ? { loadedAt: match[1], concept: match[2] } : { loadedAt: '', concept: description };
};

// Movimientos que crea invoices_collect al cobrar con banco: no vienen del banco
const isSynthetic = (mv: ReconcilableMovement) => mv.description === 'Cobro factura' && Boolean(mv.invoiceId);

// Reglas de conciliación

interface ReconRule {
  id: string;
  contains: string;
  type: 'income' | 'expense' | 'any';
  amount: number;
  account: string;
  party: string;
}

const DEFAULT_RULES: ReconRule[] = [
  { id: 'R1', contains: 'COMISION', type: 'expense', amount: 0, account: '626000', party: 'Banco' },
  { id: 'R2', contains: 'INTERES', type: 'income', amount: 0, account: '769000', party: 'Banco' },
  { id: 'R3', contains: 'TGSS', type: 'expense', amount: 0, account: '476000', party: 'Seguridad Social' },
  { id: 'R4', contains: 'AEAT', type: 'expense', amount: 0, account: '475000', party: 'Agencia Tributaria' }
];

const TYPE_LABELS: Record<ReconRule['type'], string> = { income: 'Ingreso', expense: 'Gasto', any: 'Ambos' };

const norm = (v: string) => String(v || '').toUpperCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^A-Z0-9]/g, '');

// Palabras en común entre dos conceptos, para desempatar
const sharedWords = (a: string, b: string) => {
  const target = norm(b);
  return String(a || '').toUpperCase().split(/[^A-Z0-9ÁÉÍÓÚÑ]+/).filter(w => w.length >= 4 && target.includes(norm(w))).length;
};

type Proposal =
  | { kind: 'entry'; entryId: string; reason: string }
  | { kind: 'invoice'; invoiceId: string; reason: string }
  | { kind: 'rule'; ruleId: string; account: string };

interface BankReconciliationFormProps {
  privacyMode: boolean;
}

export const BankReconciliationForm = ({ privacyMode }: BankReconciliationFormProps) => {
  const { notify } = useToast();
  const fileRef = useRef<HTMLInputElement>(null);

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [banks, setBanks] = useState<BankAccountRecord[]>([]);
  const [accounts, setAccounts] = useState<ChartAccount[]>([]);
  const [clients, setClients] = useState<BillingClient[]>([]);
  const [openInvoices, setOpenInvoices] = useState<InvoiceSummary[]>([]);
  const [invoiceNumbers, setInvoiceNumbers] = useState<Record<string, string>>({});
  const [movements, setMovements] = useState<ReconcilableMovement[]>([]);
  const [movementsLoading, setMovementsLoading] = useState(false);
  const [entries, setEntries] = useState<BankJournalEntry[]>([]);
  const [entriesLoading, setEntriesLoading] = useState(false);

  // Parámetros
  const [bankId, setBankId] = useState('');
  const [from, setFrom] = useState(`${today().slice(0, 8)}01`);
  const [statementDate, setStatementDate] = useState(today());
  const [opening, setOpening] = useState(0);
  const [closing, setClosing] = useState(0);
  const [intermediate, setIntermediate] = useState('');

  const [statement, setStatement] = useState<C43Statement | null>(null);
  const [rules, setRules] = useState<ReconRule[]>(DEFAULT_RULES);
  const [newRule, setNewRule] = useState<ReconRule>({ id: '', contains: '', type: 'expense', amount: 0, account: '', party: '' });

  // Selección y acciones por movimiento
  const [selectedMovement, setSelectedMovement] = useState('');
  const [selectedEntry, setSelectedEntry] = useState('');
  const [newTxFor, setNewTxFor] = useState('');
  const [newTxAccount, setNewTxAccount] = useState('');
  const [newTxInvoice, setNewTxInvoice] = useState('');
  const [excluded, setExcluded] = useState<string[]>([]);
  // Parejas casadas en esta sesión y cómo se concilió cada movimiento
  const [sessionPairs, setSessionPairs] = useState<Record<string, string>>({});
  const [sessionTrace, setSessionTrace] = useState<Record<string, string>>({});
  // Ya contabilizados pero sin conciliar, para no repetirlos al reintentar
  const [posted, setPosted] = useState<string[]>([]);
  const [busy, setBusy] = useState('');
  const [formError, setFormError] = useState('');

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const [bankList, chart, clientList, invoices, reconciled] = await Promise.all([
          listBankAccounts(),
          listChartOfAccounts(),
          listBillingClients(),
          listInvoices(),
          listReconcilableMovements({ reconciled: true })
        ]);
        if (cancelled) return;
        // Facturas ya casadas con un movimiento
        const used = reconciled.filter(m => !isSynthetic(m)).map(m => m.invoiceId).filter(Boolean);
        setBanks(bankList);
        setAccounts(chart);
        setClients(clientList);
        setOpenInvoices(invoices.filter(i => OPEN_INVOICE_STATUSES.includes(i.status) && i.total > 0 && !used.includes(i.id)));
        setInvoiceNumbers(invoices.reduce<Record<string, string>>((acc, i) => { acc[i.id] = i.number; return acc; }, {}));
        const first = bankList[0];
        setBankId(first ? first.id : '');
        setClosing(first ? first.balance : 0);
        const suspense = chart.find(a => a.code.startsWith('555'));
        setIntermediate(suspense ? suspense.code : '555000');
        setLoadError('');
      } catch (err) {
        console.error('Error loading bank reconciliation data:', err);
        if (!cancelled) setLoadError(err instanceof Error ? err.message : 'No se pudieron cargar los datos de conciliación.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  const loadMovements = async (id: string) => {
    if (!id) return;
    setMovementsLoading(true);
    try {
      setMovements(await listReconcilableMovements({ bankAccountId: id }));
    } catch (err) {
      console.error('Error loading bank movements:', err);
      setFormError(`No se pudieron cargar los movimientos: ${err instanceof Error ? err.message : 'error del servidor.'}`);
    } finally {
      setMovementsLoading(false);
    }
  };

  useEffect(() => {
    loadMovements(bankId);
  }, [bankId]);

  useEffect(() => {
    if (accounts.length === 0) return;
    let cancelled = false;
    setEntriesLoading(true);
    listBankJournalEntries(accounts, from, statementDate)
      .then(list => { if (!cancelled) setEntries(list); })
      .catch(err => {
        console.error('Error loading journal entries:', err);
        if (!cancelled) setFormError(`No se pudieron cargar los asientos del sistema: ${err instanceof Error ? err.message : 'error del servidor.'}`);
      })
      .finally(() => { if (!cancelled) setEntriesLoading(false); });
    return () => { cancelled = true; };
  }, [accounts, from, statementDate]);

  const bank = banks.find(b => b.id === bankId);

  const handleBankChange = (id: string) => {
    setBankId(id);
    setSelectedMovement('');
    setSelectedEntry('');
    const b = banks.find(x => x.id === id);
    if (b && !statement) setClosing(b.balance);
  };

  const accountName = (code: string) => {
    const exact = accounts.find(a => a.code === code);
    if (exact) return exact.name;
    const prefix = accounts.find(a => code.startsWith(a.code) && a.code.length >= 3);
    return prefix ? prefix.name : code;
  };

  // Columna 1: extracto. Columna 2: asientos contra la 572

  const inPeriod = (mv: ReconcilableMovement) => (!from || mv.date >= from) && (!statementDate || mv.date <= statementDate);
  const visible = movements.filter(m => !isSynthetic(m) && inPeriod(m));
  const pending = visible.filter(m => !m.reconciled && !excluded.includes(m.id));
  const done = visible.filter(m => m.reconciled);

  // No se guarda con qué asiento se casó cada movimiento: se busca por nº de factura
  // o por importe y fecha (±3 días)
  const consumedEntries: string[] = (() => {
    const consumed = done.map(m => sessionPairs[m.id]).filter(Boolean);
    // Los cobros en caja no pasan por el banco
    entries.forEach(e => {
      const cash = e.concept.match(/^COBRO EN CAJA DE .*: ([^:]+)$/i);
      if (!cash) return;
      consumed.push(e.id);
      cash[1].split(',').map(n => `COBROFACTURA${norm(n)}`).forEach(target => {
        const collection = entries.find(x => !consumed.includes(x.id) && norm(x.concept) === target);
        if (collection) consumed.push(collection.id);
      });
    });
    const byDate: ReconcilableMovement[] = [];
    done.filter(m => !sessionPairs[m.id]).forEach(mv => {
      const number = mv.invoiceId ? norm(invoiceNumbers[mv.invoiceId] || '') : '';
      const byInvoice = number.length >= 3
        ? entries.find(e => !consumed.includes(e.id) && sameAmount(e.amount, mv.amount) && norm(e.concept).includes(number))
        : undefined;
      if (byInvoice) consumed.push(byInvoice.id);
      else byDate.push(mv);
    });
    byDate
      .sort((a, b) => a.date.localeCompare(b.date))
      .forEach(mv => {
        const candidate = entries
          .filter(e => !consumed.includes(e.id) && sameAmount(e.amount, mv.amount) && daysBetween(e.date, mv.date) <= 3)
          .sort((a, b) => daysBetween(a.date, mv.date) - daysBetween(b.date, mv.date))[0];
        if (candidate) consumed.push(candidate.id);
      });
    return consumed;
  })();
  const pendingEntries = entries.filter(e => !consumedEntries.includes(e.id));

  const scoreInvoice = (mv: ReconcilableMovement, inv: InvoiceSummary) => {
    const reasons: string[] = [];
    let score = 0;
    const amount = Math.abs(mv.amount);
    const diff = Math.abs(inv.total - amount);
    if (diff < 0.005) { score += 60; reasons.push('importe exacto'); }
    else if (diff / inv.total <= 0.03) { score += 20; reasons.push(`importe similar (dif. ${diff.toFixed(2)} €)`); }
    const desc = norm(mv.description);
    const num = norm(inv.number);
    if (num.length >= 4 && desc.includes(num)) { score += 50; reasons.push('nº de factura en el concepto'); }
    const client = clients.find(c => c.id === inv.clientId);
    if (client) {
      const name = norm(client.name);
      const cif = norm(client.cif);
      if ((name.length >= 4 && desc.includes(name)) || (cif.length >= 5 && desc.includes(cif))) {
        score += 25; reasons.push('cliente en el concepto');
      }
    }
    const refDate = inv.dueDate || inv.issueDate;
    if (refDate && mv.date && daysBetween(mv.date, refDate) <= 45) { score += 10; reasons.push('fecha cercana'); }
    return { score, reasons };
  };

  const ruleFor = (mv: ReconcilableMovement) => {
    const desc = norm(mv.description);
    return rules.find(r =>
      r.contains.trim() !== '' &&
      desc.includes(norm(r.contains)) &&
      (r.type === 'any' || (r.type === 'income') === (mv.amount > 0)) &&
      (r.amount <= 0 || sameAmount(Math.abs(mv.amount), r.amount)));
  };

  // Sugerencias: asiento existente, factura pendiente o regla
  const proposals: Record<string, Proposal> = (() => {
    const result: Record<string, Proposal> = {};

    const entryCandidates: { mvId: string; entryId: string; score: number; reason: string }[] = [];
    pending.forEach(mv => {
      const scored = pendingEntries
        .filter(e => sameAmount(e.amount, mv.amount) && daysBetween(e.date, mv.date) <= 5)
        .map(e => {
          const days = daysBetween(e.date, mv.date);
          const words = sharedWords(traceOf(mv.description).concept, e.concept);
          return { e, days, words, score: 100 - days * 5 + words * 10 };
        })
        .sort((a, b) => b.score - a.score);
      if (scored.length === 0) return;
      if (scored.length > 1 && scored[0].score === scored[1].score) return;
      const best = scored[0];
      const reason = `importe exacto, ${best.days === 0 ? 'misma fecha' : `fecha ±${Math.round(best.days)}d`}${best.words > 0 ? ', concepto parecido' : ''}`;
      entryCandidates.push({ mvId: mv.id, entryId: best.e.id, score: best.score, reason });
    });
    entryCandidates.sort((a, b) => b.score - a.score);
    const takenEntries: string[] = [];
    entryCandidates.forEach(c => {
      if (takenEntries.includes(c.entryId)) return;
      takenEntries.push(c.entryId);
      result[c.mvId] = { kind: 'entry', entryId: c.entryId, reason: c.reason };
    });

    const invoiceCandidates: { mvId: string; invoiceId: string; score: number; reason: string }[] = [];
    pending.forEach(mv => {
      if (result[mv.id] || mv.amount <= 0) return;
      const scored = openInvoices
        .map(inv => ({ id: inv.id, ...scoreInvoice(mv, inv) }))
        .filter(x => x.score >= 60)
        .sort((a, b) => b.score - a.score);
      if (scored.length === 0) return;
      if (scored.length > 1 && scored[0].score - scored[1].score < 10) return;
      invoiceCandidates.push({ mvId: mv.id, invoiceId: scored[0].id, score: scored[0].score, reason: scored[0].reasons.join(', ') });
    });
    invoiceCandidates.sort((a, b) => b.score - a.score);
    const takenInvoices: string[] = [];
    invoiceCandidates.forEach(c => {
      if (takenInvoices.includes(c.invoiceId)) return;
      takenInvoices.push(c.invoiceId);
      result[c.mvId] = { kind: 'invoice', invoiceId: c.invoiceId, reason: c.reason };
    });

    pending.forEach(mv => {
      if (result[mv.id]) return;
      const rule = ruleFor(mv);
      if (rule) result[mv.id] = { kind: 'rule', ruleId: rule.id, account: rule.account };
    });
    return result;
  })();

  // Motivo de lo que queda por revisar
  const reviewReason = (mv: ReconcilableMovement) => {
    const sameAmountEntries = pendingEntries.filter(e => sameAmount(e.amount, mv.amount));
    if (sameAmountEntries.length > 1) return `${sameAmountEntries.length} asientos con el mismo importe: elija cuál con "Buscar match".`;
    if (mv.amount < 0) return 'Gasto sin asiento ni regla: créelo con "Nueva transacción".';
    const amount = Math.abs(mv.amount);
    const exact = openInvoices.filter(i => sameAmount(i.total, amount));
    if (exact.length > 1) return `${exact.length} facturas con el mismo importe: elija cuál en "Nueva transacción".`;
    const similar = openInvoices.find(i => Math.abs(i.total - amount) / i.total <= 0.03);
    if (similar) return `Se parece a ${similar.number} (${formatEuro(similar.total, privacyMode)}): posible comisión.`;
    return 'Sin asiento, factura ni regla: si no se sabe qué es, envíelo a la cuenta intermedia.';
  };

  // Saldo conciliado = saldo inicial + movimientos conciliados

  const reconciledBalance = round2(opening + done.reduce((s, m) => s + m.amount, 0));
  const difference = round2(closing - reconciledBalance);
  const readyToClose = difference === 0;

  // Carga del Cuaderno 43 (solo crea los movimientos)

  const isLoaded = (mv: C43Movement, existing: ReconcilableMovement[] = movements) => existing.some(m =>
    m.date === mv.date && sameAmount(m.amount, mv.amount) && traceOf(m.description).concept === mv.concept.replace(/'/g, ''));

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const parsed = parseC43(String(reader.result || ''), file.name);
        setStatement(parsed);
        if (parsed.from) setFrom(parsed.from);
        if (parsed.to) setStatementDate(parsed.to);
        setOpening(parsed.opening);
        setClosing(parsed.closing);
        setFormError('');
      } catch (err) {
        setFormError(err instanceof Error ? err.message : 'No se pudo leer el fichero.');
      }
    };
    reader.readAsText(file, 'latin1');
  };

  const handleLoadStatement = async () => {
    if (!statement || !bank) return;
    setBusy('c43');
    setFormError('');
    // Se vuelve a leer la cuenta: el estado local puede estar desfasado o aún cargando
    let existing = movements;
    try {
      existing = await listReconcilableMovements({ bankAccountId: bank.id });
      setMovements(existing);
    } catch (err) {
      console.error('Error refreshing bank movements:', err);
      setFormError('No se pudo comprobar qué movimientos ya estaban cargados; no se ha cargado nada.');
      setBusy('');
      return;
    }
    const toLoad = statement.movements.filter(mv => !isLoaded(mv, existing));
    const skippedBefore = statement.movements.length - toLoad.length;
    if (toLoad.length === 0) {
      notify('Cuaderno 43', `Los ${skippedBefore} movimiento(s) del fichero ya estaban cargados: no se ha cargado nada.`, 'warning');
      setBusy('');
      return;
    }
    let loaded = 0;
    let duplicates = 0;
    for (const mv of toLoad) {
      try {
        await importBankMovement(bank.id, { description: `[C43 ${today()}] ${mv.concept}`, amount: mv.amount, date: mv.date });
        loaded++;
      } catch (err) {
        // El backend también rechaza los duplicados: se omiten y se avisa, sin abortar la carga
        if (err instanceof DuplicateBankMovementError) {
          duplicates++;
          continue;
        }
        console.error('Error loading C43 movement:', err);
        setFormError(`Se cargaron ${loaded} de ${toLoad.length - duplicates} movimientos. Falló "${mv.concept}": ${err instanceof Error ? err.message : 'error del servidor.'}`);
        break;
      }
    }
    if (loaded + duplicates === toLoad.length) {
      const omitted = skippedBefore + duplicates;
      if (loaded > 0) notify('Cuaderno 43 cargado', `${loaded} movimiento(s) del extracto cargados en ${bank.name}, pendientes de conciliar.`, 'info');
      if (omitted > 0) notify('Cuaderno 43', `${omitted} movimiento(s) ya estaban cargados y se han omitido para no duplicarlos.`, 'warning');
      setStatement(null);
    }
    setBusy('');
    loadMovements(bank.id);
  };

  const markDone = (mv: ReconcilableMovement, invoiceId: string, how: string, entryId = '') => {
    setMovements(prev => prev.map(m => (m.id === mv.id ? { ...m, reconciled: true, invoiceId } : m)));
    if (entryId) setSessionPairs(prev => ({ ...prev, [mv.id]: entryId }));
    setSessionTrace(prev => ({ ...prev, [mv.id]: how }));
    if (selectedMovement === mv.id) setSelectedMovement('');
    if (entryId && selectedEntry === entryId) setSelectedEntry('');
    if (newTxFor === mv.id) setNewTxFor('');
  };

  // Casar con un asiento existente: no se contabiliza nada
  const matchPair = async (mv: ReconcilableMovement, entryId: string, how: string): Promise<boolean> => {
    const entry = entries.find(e => e.id === entryId);
    if (!entry || !sameAmount(entry.amount, mv.amount)) {
      setFormError('Los importes no coinciden: el asiento y el movimiento deben ser del mismo importe.');
      return false;
    }
    setBusy(mv.id);
    setFormError('');
    try {
      await reconcileBankMovement(mv.id);
    } catch (err) {
      console.error('Error reconciling bank movement:', err);
      setFormError(`No se pudo conciliar el movimiento: ${err instanceof Error ? err.message : 'error del servidor.'}`);
      setBusy('');
      return false;
    }
    markDone(mv, '', `${how} · casado con ${entryLabel(entry.id)}`, entry.id);
    setBusy('');
    return true;
  };

  // Transacción nueva contra la 572. El cobro de una factura lo contabiliza invoices_collect
  const createTransaction = async (mv: ReconcilableMovement, target: { account?: string; invoiceId?: string }, how: string): Promise<boolean> => {
    if (!bank) return false;
    const invoice = target.invoiceId ? openInvoices.find(i => i.id === target.invoiceId) : undefined;
    if (invoice) return collectFromBank(mv, invoice, how);
    const counter = target.account || '';
    if (!counter) {
      setFormError('Elija la cuenta o la factura de la transacción.');
      return false;
    }
    const amount = Math.abs(mv.amount);
    const bankLine = { account: '572000', accountName: `Bancos c/c (${bank.name})` };
    const counterLine = { account: counter, accountName: accountName(counter) };
    const lines: JournalLine[] = mv.amount > 0
      ? [{ ...bankLine, debit: amount, credit: 0 }, { ...counterLine, debit: 0, credit: amount }]
      : [{ ...counterLine, debit: amount, credit: 0 }, { ...bankLine, debit: 0, credit: amount }];

    setBusy(mv.id);
    setFormError('');
    let entryPosted = posted.includes(mv.id);
    try {
      if (!entryPosted) {
        const concept = `Conciliación ${bank.name}: ${traceOf(mv.description).concept} · ${how}`;
        await postJournalEntry(concept, lines, 'Conciliación Bancaria', accounts, mv.date);
        entryPosted = true;
        setPosted(prev => [...prev, mv.id]);
      }
      await reconcileBankMovement(mv.id);
    } catch (err) {
      console.error('Error creating reconciliation transaction:', err);
      const state = entryPosted ? 'El asiento ya está contabilizado, pero el movimiento no quedó conciliado' : 'No se pudo crear la transacción';
      setFormError(`${state}: ${err instanceof Error ? err.message : 'error del servidor.'} Pulse de nuevo para reintentar.`);
      setBusy('');
      return false;
    }

    setPosted(prev => prev.filter(id => id !== mv.id));
    markDone(mv, '', how);
    setBusy('');
    return true;
  };

  // Sin cuenta bancaria para que no se cree otro movimiento
  const collectFromBank = async (mv: ReconcilableMovement, invoice: InvoiceSummary, how: string): Promise<boolean> => {
    setBusy(mv.id);
    setFormError('');
    let collected = posted.includes(mv.id);
    try {
      if (!collected) {
        await collectInvoice(invoice.id, { reference: traceOf(mv.description).concept, bankAccountId: '' });
        collected = true;
        setPosted(prev => [...prev, mv.id]);
      }
      await reconcileBankMovement(mv.id, invoice.id);
    } catch (err) {
      console.error('Error collecting invoice from bank movement:', err);
      const state = collected ? `La factura ${invoice.number} ya está cobrada, pero el movimiento no quedó conciliado` : `No se pudo cobrar la factura ${invoice.number}`;
      setFormError(`${state}: ${err instanceof Error ? err.message : 'error del servidor.'} Pulse de nuevo para reintentar.`);
      setBusy('');
      return false;
    }
    setOpenInvoices(prev => prev.filter(i => i.id !== invoice.id));
    setPosted(prev => prev.filter(id => id !== mv.id));
    markDone(mv, invoice.id, how);
    setBusy('');
    return true;
  };

  const applyProposal = (mv: ReconcilableMovement): Promise<boolean> => {
    const p = proposals[mv.id];
    if (!p) return Promise.resolve(false);
    if (p.kind === 'entry') return matchPair(mv, p.entryId, `sugerencia (${p.reason})`);
    if (p.kind === 'invoice') {
      const inv = openInvoices.find(i => i.id === p.invoiceId);
      return createTransaction(mv, { invoiceId: p.invoiceId }, `cobro de ${inv ? inv.number : 'factura'} por regla (${p.reason})`);
    }
    const rule = rules.find(r => r.id === p.ruleId);
    return createTransaction(mv, { account: p.account }, `regla "${rule ? rule.contains : ''}"`);
  };

  const handleConfirmAll = async () => {
    const targets = pending.filter(m => proposals[m.id]);
    if (targets.length === 0) return;
    if (!window.confirm(`Se van a aplicar ${targets.length} sugerencia(s). Las de asiento solo se casan; las de factura o regla crean su asiento. ¿Continuar?`)) return;
    let applied = 0;
    for (const mv of targets) {
      if (!(await applyProposal(mv))) break;
      applied++;
    }
    if (applied === targets.length) notify('Conciliación', `${applied} movimiento(s) conciliados.`, 'info');
  };

  const handleSelectMovement = (mv: ReconcilableMovement) => {
    if (selectedMovement === mv.id) {
      setSelectedMovement('');
      return;
    }
    setSelectedMovement(mv.id);
    const p = proposals[mv.id];
    setSelectedEntry(p && p.kind === 'entry' ? p.entryId : '');
  };

  const openNewTransaction = (mv: ReconcilableMovement) => {
    const p = proposals[mv.id];
    setNewTxFor(mv.id);
    setNewTxAccount(p && p.kind === 'rule' ? p.account : '');
    setNewTxInvoice(p && p.kind === 'invoice' ? p.invoiceId : '');
  };

  const addRule = () => {
    if (!newRule.contains.trim() || !newRule.account) {
      setFormError('La regla necesita un texto del concepto y una cuenta.');
      return;
    }
    setRules(prev => [...prev, { ...newRule, id: `R${Date.now()}`, contains: newRule.contains.trim() }]);
    setNewRule({ id: '', contains: '', type: 'expense', amount: 0, account: '', party: '' });
    setFormError('');
  };

  if (loading) return <FormSkeleton />;

  if (loadError) {
    return (
      <div className="module-panel">
        <div className="module-panel-empty">{loadError}</div>
      </div>
    );
  }

  const accountOptions = accounts
    .filter(a => a.code.length >= 3)
    .map(a => ({ value: a.code, label: `${a.code} · ${a.name}` }));
  const counterOptions = accountOptions.filter(o => !o.value.startsWith('572'));
  const invoiceLabel = (inv: InvoiceSummary) => {
    const client = clients.find(c => c.id === inv.clientId);
    return `${inv.number} · ${client ? client.name : `Cliente #${inv.clientId}`} · ${formatEuro(inv.total, privacyMode)}`;
  };

  const selectedMv = pending.find(m => m.id === selectedMovement);
  const selectedEn = pendingEntries.find(e => e.id === selectedEntry);
  // Con un movimiento elegido, la columna 2 se ordena por parecido
  const sortedEntries = selectedMv
    ? [...pendingEntries].sort((a, b) =>
        (Math.abs(a.amount - selectedMv.amount) - Math.abs(b.amount - selectedMv.amount)) ||
        (daysBetween(a.date, selectedMv.date) - daysBetween(b.date, selectedMv.date)))
    : pendingEntries;
  const sortedPending = [...pending].sort((a, b) => Number(Boolean(proposals[b.id])) - Number(Boolean(proposals[a.id])));
  const proposalCount = pending.filter(m => proposals[m.id]).length;

  const renderProposal = (mv: ReconcilableMovement) => {
    const p = proposals[mv.id];
    if (!p) return <><span className="fin-recon__tag review">Por revisar</span><span className="fin-recon__muted">{reviewReason(mv)}</span></>;
    if (p.kind === 'entry') {
      const entry = entries.find(e => e.id === p.entryId);
      return (
        <>
          <span className="fin-recon__tag match">Sugerencia: asiento del sistema</span>
          <span className="fin-recon__muted">{entry ? `${entryLabel(entry.id)} · ${entry.concept}` : ''} ({p.reason})</span>
        </>
      );
    }
    if (p.kind === 'invoice') {
      const inv = openInvoices.find(i => i.id === p.invoiceId);
      return (
        <>
          <span className="fin-recon__tag invoice">Sugerencia: cobro de factura</span>
          <span className="fin-recon__muted">{inv ? invoiceLabel(inv) : ''} ({p.reason})</span>
        </>
      );
    }
    const rule = rules.find(r => r.id === p.ruleId);
    return (
      <>
        <span className="fin-recon__tag rule">Regla: "{rule ? rule.contains : ''}"</span>
        <span className="fin-recon__muted">{p.account} · {accountName(p.account)}{rule && rule.party ? ` · ${rule.party}` : ''}</span>
      </>
    );
  };

  return (
    <div className="fin-doc">
      <div className="fin-doc__header">
        <div>
          <span className="fin-doc__tag">TESORERÍA Y BANCOS • CONCILIACIÓN</span>
          <h3 className="fin-doc__title"><Landmark size={14} /> Conciliación bancaria</h3>
        </div>
        <div className="fin-recon__header-actions">
          <span className="fin-doc__badge">572000 · {bank ? bank.name : '—'}</span>
          <button
            type="button"
            className="fin-doc__btn fin-doc__btn--primary fin-recon__import"
            disabled={Boolean(busy)}
            onClick={() => fileRef.current && fileRef.current.click()}
          >
            <UploadCloud size={14} /> Importar Cuaderno 43
          </button>
        </div>
      </div>

      {formError && (
        <div className="module-form-error">
          <AlertCircle size={14} style={{ flexShrink: 0 }} />
          <span>{formError}</span>
        </div>
      )}

      {/* Parámetros */}
      <div className="fin-doc__grid">
        <div className="sidebar-form-group">
          <label className="sidebar-form-label">Cuenta bancaria (572000) *</label>
          <select className="sidebar-form-input" value={bankId} onChange={(e) => handleBankChange(e.target.value)}>
            {banks.length === 0 && <option value="">No hay cuentas bancarias</option>}
            {banks.map(b => <option key={b.id} value={b.id}>572000 · {b.name}</option>)}
          </select>
        </div>
        <div className="sidebar-form-group">
          <label className="sidebar-form-label">Desde</label>
          <input type="date" className="sidebar-form-input" value={from} onChange={(e) => setFrom(e.target.value)} />
        </div>
        <div className="sidebar-form-group">
          <label className="sidebar-form-label">Fecha de extracto *</label>
          <input type="date" className="sidebar-form-input" value={statementDate} onChange={(e) => setStatementDate(e.target.value)} />
        </div>
        <div className="sidebar-form-group">
          <label className="sidebar-form-label">Saldo inicial del extracto (€)</label>
          <input type="number" step="0.01" className="sidebar-form-input" value={opening} onChange={(e) => setOpening(parseFloat(e.target.value) || 0)} />
        </div>
        <div className="sidebar-form-group">
          <label className="sidebar-form-label">Saldo final del extracto (€) *</label>
          <input type="number" step="0.01" className="sidebar-form-input" value={closing} onChange={(e) => setClosing(parseFloat(e.target.value) || 0)} />
        </div>
        <div className="sidebar-form-group">
          <label className="sidebar-form-label">Cuenta intermedia de consolidación</label>
          <SearchSelect
            options={accountOptions}
            value={intermediate}
            onChange={setIntermediate}
            placeholder="Sugerida: 555000"
            emptyText="No hay cuentas"
          />
        </div>
      </div>

      {/* Resumen en tiempo real */}
      <div className="fin-recon__summary">
        <div className="fin-recon__stat">
          <span>Saldo extracto</span>
          <strong>{formatEuro(closing, privacyMode)}</strong>
        </div>
        <div className="fin-recon__stat">
          <span>Saldo conciliado</span>
          <strong>{formatEuro(reconciledBalance, privacyMode)}</strong>
        </div>
        <div className={`fin-recon__stat ${readyToClose ? 'ok' : 'ko'}`}>
          <span>Diferencia</span>
          <strong>{formatEuro(difference, privacyMode)}</strong>
        </div>
        <button
          type="button"
          className="fin-doc__btn fin-doc__btn--primary"
          disabled
          title={readyToClose ? 'Lista para cerrar: pendiente de endpoint de cierre' : 'La diferencia debe ser 0,00 €'}
        >
          Cerrar conciliación
        </button>
      </div>
      <div className={`fin-doc__balance ${readyToClose ? 'ok' : 'ko'}`}>
        {readyToClose ? <CheckCircle2 size={14} /> : <AlertCircle size={14} />}
        {readyToClose
          ? `Diferencia 0,00 €: el extracto a ${formatDate(statementDate)} cuadra con lo conciliado.`
          : `Faltan ${formatEuro(Math.abs(difference), privacyMode)} por conciliar para cerrar a ${formatDate(statementDate)}.`}
      </div>

      {/* Carga del Cuaderno 43 */}
      <div className="fin-recon__section">
        <div className="fin-recon__section-title">
          <span>Cuaderno 43</span>
        </div>
        <input ref={fileRef} type="file" accept=".c43,.n43,.txt,.aeb,.csb" className="fin-recon__file" onChange={handleFile} />
        {statement ? (
          <div className="fin-recon__pairbar">
            <span>
              {statement.fileName} · {formatDate(statement.from)} a {formatDate(statement.to)} · {statement.movements.length} movimiento(s),{' '}
              {statement.movements.filter(mv => isLoaded(mv)).length} ya cargado(s)
            </span>
            <span className="fin-recon__actions">
              <button type="button" className="fin-doc__btn" disabled={Boolean(busy)} onClick={() => setStatement(null)}>Descartar</button>
              <button type="button" className="fin-doc__btn fin-doc__btn--primary" disabled={Boolean(busy)} onClick={handleLoadStatement}>
                {busy === 'c43' ? 'Cargando...' : `Cargar en ${bank ? bank.name : 'el banco'}`}
              </button>
            </span>
          </div>
        ) : (
          <span className="fin-recon__muted">
            Use "Importar Cuaderno 43": el fichero del banco rellena las fechas y los saldos y crea los movimientos del extracto, pendientes de conciliar. No se contabiliza nada hasta conciliarlos.
          </span>
        )}
      </div>

      {/* Reglas de conciliación automática */}
      <div className="fin-recon__section">
        <div className="fin-recon__section-title">
          <span>Reglas de conciliación automática (concepto / ordenante, tipo, importe → cuenta)</span>
        </div>
        {rules.map(r => (
          <div key={r.id} className="fin-recon__rule">
            <span>Concepto contiene <strong>"{r.contains}"</strong></span>
            <span>{TYPE_LABELS[r.type]}</span>
            <span>{r.amount > 0 ? formatEuro(r.amount, privacyMode) : 'Cualquier importe'}</span>
            <span>{r.account} · {accountName(r.account)}</span>
            <span className="fin-recon__muted">{r.party || '—'}</span>
            <button type="button" className="fin-lines__remove" title="Quitar regla" onClick={() => setRules(rules.filter(x => x.id !== r.id))}>
              <Trash2 size={13} />
            </button>
          </div>
        ))}
        <div className="fin-recon__rule">
          <input
            type="text"
            className="sidebar-form-input"
            value={newRule.contains}
            onChange={(e) => setNewRule({ ...newRule, contains: e.target.value })}
            placeholder="Texto del concepto u ordenante"
          />
          <select className="sidebar-form-input" value={newRule.type} onChange={(e) => setNewRule({ ...newRule, type: e.target.value as ReconRule['type'] })}>
            <option value="expense">Gasto</option>
            <option value="income">Ingreso</option>
            <option value="any">Ambos</option>
          </select>
          <input
            type="number"
            min={0}
            step="0.01"
            className="sidebar-form-input"
            value={newRule.amount}
            onChange={(e) => setNewRule({ ...newRule, amount: parseFloat(e.target.value) || 0 })}
            title="0 = cualquier importe"
          />
          <SearchSelect
            options={counterOptions}
            value={newRule.account}
            onChange={(account) => setNewRule({ ...newRule, account })}
            placeholder="Cuenta destino..."
            emptyText="No hay cuentas"
          />
          <input
            type="text"
            className="sidebar-form-input"
            value={newRule.party}
            onChange={(e) => setNewRule({ ...newRule, party: e.target.value })}
            placeholder="Proveedor / cliente"
          />
          <button type="button" className="fin-lines__add" title="Añadir regla" onClick={addRule}>
            <Plus size={12} />
          </button>
        </div>
        <span className="fin-recon__muted">Las reglas se aplican solo en esta sesión: no hay endpoint para guardarlas.</span>
      </div>

      {/* Pareja elegida en las dos columnas */}
      <div className="fin-recon__section">
        <div className="fin-recon__section-title">
          <span>Pendientes: {pending.length} del extracto · {pendingEntries.length} del sistema · por revisar: {pending.filter(m => !proposals[m.id]).length}</span>
          {proposalCount > 0 && (
            <button type="button" className="fin-doc__btn fin-doc__btn--primary" disabled={Boolean(busy)} onClick={handleConfirmAll}>
              Aplicar {proposalCount} sugerencia(s)
            </button>
          )}
        </div>
        {(selectedMv || selectedEn) && (
          <div className="fin-recon__pairbar">
            <span>
              <Link2 size={12} /> {selectedMv ? `${traceOf(selectedMv.description).concept} (${formatEuro(selectedMv.amount, privacyMode)})` : 'Elija un movimiento del extracto'}
              {' ↔ '}
              {selectedEn ? `${entryLabel(selectedEn.id)} ${selectedEn.concept} (${formatEuro(selectedEn.amount, privacyMode)})` : 'elija un asiento del sistema'}
              {selectedMv && selectedEn && !sameAmount(selectedMv.amount, selectedEn.amount) && ' · los importes no coinciden'}
            </span>
            <button
              type="button"
              className="fin-doc__btn fin-doc__btn--primary"
              disabled={!selectedMv || !selectedEn || !sameAmount(selectedMv.amount, selectedEn.amount) || Boolean(busy)}
              onClick={() => selectedMv && selectedEn && matchPair(selectedMv, selectedEn.id, 'manual')}
            >
              Conciliar pareja
            </button>
          </div>
        )}
      </div>

      {/* Dos columnas comparativas */}
      <div className="fin-recon__columns">
        <div className="fin-recon__column">
          <div className="fin-recon__section-title">
            <span>1. Extracto bancario (Cuaderno 43 / manuales)</span>
            <span>{done.length} / {visible.length} conciliados</span>
          </div>

          {movementsLoading ? (
            <div className="fin-lines__empty">Cargando movimientos...</div>
          ) : visible.length === 0 ? (
            <div className="fin-lines__empty">No hay movimientos del banco en el periodo.</div>
          ) : (
            <>
              {sortedPending.map(mv => {
                const trace = traceOf(mv.description);
                const isBusy = busy === mv.id;
                return (
                  <div key={mv.id} className={`fin-recon__item ${selectedMovement === mv.id ? 'is-selected' : ''}`}>
                    <input
                      type="checkbox"
                      checked={selectedMovement === mv.id}
                      onChange={() => handleSelectMovement(mv)}
                      title="Elegir para conciliar"
                    />
                    <div className="fin-recon__item-body">
                      <div className="fin-recon__item-top">
                        <span>{trace.concept || 'Movimiento bancario'}</span>
                        <span className={`fin-recon__amount ${mv.amount > 0 ? 'income' : 'expense'}`}>{formatEuro(mv.amount, privacyMode)}</span>
                      </div>
                      <span className="fin-recon__muted">
                        {formatDate(mv.date)} · Banco: {bank ? bank.name : '—'} · {trace.loadedAt ? `Cargado ${formatDate(trace.loadedAt)} (C43)` : 'Alta manual'}
                      </span>
                      {renderProposal(mv)}

                      {newTxFor === mv.id ? (
                        <div className="fin-recon__panel">
                          <SearchSelect
                            options={counterOptions}
                            value={newTxAccount}
                            onChange={(account) => { setNewTxAccount(account); setNewTxInvoice(''); }}
                            placeholder="Cuenta de contrapartida..."
                            emptyText="No hay cuentas"
                          />
                          {mv.amount > 0 && (
                            <select
                              className="sidebar-form-input"
                              value={newTxInvoice}
                              onChange={(e) => { setNewTxInvoice(e.target.value); if (e.target.value) setNewTxAccount(''); }}
                            >
                              <option value="">o cobro de una factura pendiente...</option>
                              {openInvoices
                                .slice()
                                .sort((a, b) => scoreInvoice(mv, b).score - scoreInvoice(mv, a).score)
                                .map(inv => <option key={inv.id} value={inv.id}>{invoiceLabel(inv)}</option>)}
                            </select>
                          )}
                          <div className="fin-recon__actions">
                            <button
                              type="button"
                              className="fin-doc__btn fin-doc__btn--primary"
                              disabled={isBusy || Boolean(busy) || (!newTxAccount && !newTxInvoice)}
                              onClick={() => createTransaction(mv, newTxInvoice ? { invoiceId: newTxInvoice } : { account: newTxAccount }, 'nueva transacción (manual)')}
                            >
                              {isBusy ? 'Creando...' : 'Crear y conciliar'}
                            </button>
                            <button type="button" className="fin-doc__btn" onClick={() => setNewTxFor('')}>Cancelar</button>
                          </div>
                        </div>
                      ) : (
                        <div className="fin-recon__actions">
                          {proposals[mv.id] && (
                            <button type="button" className="fin-doc__btn fin-doc__btn--primary" disabled={Boolean(busy)} onClick={() => applyProposal(mv)}>
                              {isBusy ? 'Conciliando...' : 'Conciliar'}
                            </button>
                          )}
                          <button type="button" className="fin-doc__btn fin-doc__btn--outline" disabled={Boolean(busy)} onClick={() => handleSelectMovement(mv)}>
                            Buscar match
                          </button>
                          <button type="button" className="fin-doc__btn fin-doc__btn--outline" disabled={Boolean(busy)} onClick={() => openNewTransaction(mv)}>
                            Nueva transacción
                          </button>
                          <button
                            type="button"
                            className="fin-doc__btn fin-doc__btn--outline"
                            disabled={Boolean(busy) || !intermediate}
                            title="Partida pendiente de aplicación: se contabiliza contra la cuenta intermedia"
                            onClick={() => createTransaction(mv, { account: intermediate }, 'pendiente de aplicación (cuenta intermedia)')}
                          >
                            A cuenta {intermediate.slice(0, 3)}
                          </button>
                          <button
                            type="button"
                            className="fin-doc__btn"
                            disabled={Boolean(busy)}
                            title="Lo oculta en esta sesión: no hay endpoint para excluirlo"
                            onClick={() => setExcluded(prev => [...prev, mv.id])}
                          >
                            Excluir
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}

              {done.map(mv => {
                const trace = traceOf(mv.description);
                return (
                  <div key={mv.id} className="fin-recon__item is-done">
                    <input type="checkbox" checked disabled title="Conciliado" />
                    <div className="fin-recon__item-body">
                      <div className="fin-recon__item-top">
                        <span>{trace.concept || 'Movimiento bancario'}</span>
                        <span className={`fin-recon__amount ${mv.amount > 0 ? 'income' : 'expense'}`}>{formatEuro(mv.amount, privacyMode)}</span>
                      </div>
                      <span className="fin-recon__muted">
                        {formatDate(mv.date)} · Banco: {bank ? bank.name : '—'} · {trace.loadedAt ? `Cargado ${formatDate(trace.loadedAt)} (C43)` : 'Alta manual'}
                      </span>
                      <span className="fin-recon__muted">
                        Conciliado{sessionTrace[mv.id] ? ` · ${sessionTrace[mv.id]}` : ''}{mv.invoiceId ? ` · factura #${mv.invoiceId}` : ''}
                      </span>
                    </div>
                  </div>
                );
              })}
            </>
          )}

          {excluded.length > 0 && (
            <span className="fin-recon__muted">
              {excluded.length} movimiento(s) excluidos en esta sesión.{' '}
              <button type="button" className="fin-lines__add" onClick={() => setExcluded([])}>Volver a mostrarlos</button>
            </span>
          )}
        </div>

        <div className="fin-recon__column">
          <div className="fin-recon__section-title">
            <span>2. Transacciones registradas en el sistema (572)</span>
            <span>{pendingEntries.length} pendientes de casar · {entries.length - pendingEntries.length} casadas</span>
          </div>

          {entriesLoading ? (
            <div className="fin-lines__empty">Cargando asientos del Libro Diario...</div>
          ) : pendingEntries.length === 0 ? (
            <div className="fin-lines__empty">No hay asientos contra bancos pendientes de casar en el periodo.</div>
          ) : (
            sortedEntries.map(entry => {
              const isMatchForSelected = selectedMv && sameAmount(entry.amount, selectedMv.amount);
              return (
                <div key={entry.id} className={`fin-recon__item ${selectedEntry === entry.id ? 'is-selected' : ''}`}>
                  <input
                    type="checkbox"
                    checked={selectedEntry === entry.id}
                    onChange={() => setSelectedEntry(selectedEntry === entry.id ? '' : entry.id)}
                    title="Elegir para conciliar"
                  />
                  <div className="fin-recon__item-body">
                    <div className="fin-recon__item-top">
                      <span>{entry.concept || 'Asiento'}</span>
                      <span className={`fin-recon__amount ${entry.amount > 0 ? 'income' : 'expense'}`}>{formatEuro(entry.amount, privacyMode)}</span>
                    </div>
                    <span className="fin-recon__muted">
                      {entryLabel(entry.id)} · {formatDate(entry.date)} · {entry.sourceModule || 'Libro Diario'}
                      {entry.counterparts.length > 0 ? ` · contra ${entry.counterparts.join(', ')}` : ''}
                    </span>
                    {isMatchForSelected && <span className="fin-recon__tag match">Mismo importe que el movimiento elegido</span>}
                  </div>
                </div>
              );
            })
          )}
          <span className="fin-recon__muted">
            Las cuentas bancarias contabilizan todas en la 572000: aquí salen los asientos de todos los bancos.
          </span>
        </div>
      </div>
    </div>
  );
};

export default BankReconciliationForm;
